/******************************************** STYLING *********************************************/
const s = require('./sized-text.scss');

/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import cn from 'classnames';

/**************************************** PROJECT MODULES *****************************************/
import {switchExpr, isBoolean, IfFalsy} from 'mad-utils/lib/react';

/**************************************** TYPE DEFINITIONS ****************************************/
export interface TextBlockProps {
    children: string;
    disabled?: boolean;
    mergeClasses?: string | string[];
    center?: boolean;
    style?: React.CSSProperties;
}

export interface TextBlockUnsizedArgs extends TextBlockProps {
    size: 'small' | 'tiny' | 'medium' | 'large' | 'very-large';
}

/********************************************* EXPORT *********************************************/

/**
 * Display message containing [text] (or [children]) if something isn't
 * disabling its display
 *
 * Display at the given [size]: "tiny", "small", "medium", or "large"
 *
 * Must receive [children] prop
 *
 * @param {boolean} disabled? If true, don't display the component
 * @param {string|string[]} mergeClasses Additional CSS classes to apply to the
 *                                       text wrapper {OPTIONAL}
 * @param {string} children? Text to display
 * @param {boolean} center? If true, center the text, otherwise align left
 * @param {string} size `tiny` or `small`
 */
export const TextBlock = ({
    disabled,
    mergeClasses,
    style,
    center,
    size,
    children,
}: TextBlockUnsizedArgs) => {
    return (
        <IfFalsy test={disabled}>
            <div
                style={style}
                className={cn(
                    mergeClasses,
                    `fw-300 better-helvetica`,

                    // prettier-ignore
                    switchExpr(
                        size === `tiny`,        s[`tiny-text`],
                        size === `small`,       s[`small-text`],
                        size === `medium`,      s[`medium-text`],
                        size === `large`,       s[`large-text`],
                        size === `very-large`,  s[`very-large-text`],
                                                s[`very-large-text`]
                    ),
                    center ? `ta-center` : [`flex align-self-start`, `ta-left`]
                )}
                aria-label={children}
            >
                {children}
            </div>
        </IfFalsy>
    );
};

TextBlock.displayName = `TextBlock`;

/**
 * Convenience component to create block of large-sized text (text size: level 1)
 */
export const VeryLargeText = (props: TextBlockProps) => {
    const {center, ...restProps} = props;
    const cleanCenter = isBoolean(center) ? center : true;
    return <TextBlock size="very-large" center={cleanCenter} {...restProps} />;
};

VeryLargeText.displayName = `VeryLargeText`;

/**
 * Convenience component to create block of large-sized text (text size: level 2)
 */
export const LargeText = (props: TextBlockProps) => <TextBlock size="large" {...props} />;

LargeText.displayName = `LargeText`;

/**
 * Convenience component to create block of medium-sized text (text size: level 3)
 */
export const MediumText = (props: TextBlockProps) => <TextBlock size="medium" {...props} />;

MediumText.displayName = `MediumText`;

/**
 * Convenience component to create block of small text (text size: level 4)
 */
export const SmallText = (props: TextBlockProps) => <TextBlock size="small" {...props} />;

SmallText.displayName = `SmallText`;

/**
 * Convenience component to create block of very tiny text (text size: level 5)
 */
export const TinyText = (props: TextBlockProps) => <TextBlock size="tiny" {...props} />;

TinyText.displayName = `TinyText`;
